import { useCallback, useEffect, useState } from 'react';
import {
  ensureInstallPromptListener,
  hasDeferredInstallPrompt,
  promptDeferredInstall,
  subscribeDeferredInstallPrompt,
} from '../lsc/hooks/pwaInstallPromptStore';

/** LSC League Home / Account Settings banner dismiss key (existing). */
export const LSC_INSTALL_DISMISS_KEY = 'lsc_install_banner_dismissed_at';
/** Studio Mobile App banner dismiss key — independent of LSC. */
export const STUDIO_INSTALL_DISMISS_KEY = 'studio_install_banner_dismissed_at';
/** Default remains the LSC key so existing LSC imports stay stable. */
export const INSTALL_DISMISS_KEY = LSC_INSTALL_DISMISS_KEY;

export const INSTALL_BANNER_DISMISS_TTL_DAYS = 14;
export const INSTALL_BANNER_DISMISS_TTL_MS =
  INSTALL_BANNER_DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined';
}

function mediaMatches(query: string): boolean {
  if (!isBrowser() || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export function detectStandaloneMode(): boolean {
  if (!isBrowser()) return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayStandalone = mediaMatches('(display-mode: standalone)');
  return iosStandalone || displayStandalone;
}

export function detectIos(): boolean {
  if (!isBrowser()) return false;
  const ua = navigator.userAgent;
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua);
  const isIpadOs =
    navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number'
      ? navigator.maxTouchPoints > 1
      : false;
  return isAppleMobile || isIpadOs;
}

/**
 * Safari (not Chrome/Firefox/Edge) on iOS — the only iOS browser that can
 * Add to Home Screen. CriOS/FxiOS/EdgiOS still include "Safari" in UA.
 */
export function detectIosSafari(): boolean {
  if (!detectIos()) return false;
  const ua = navigator.userAgent;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(ua)) return false;
  return true;
}

/** Android-like Chrome context for install troubleshooting copy (not iOS). */
export function detectAndroidInstallContext(): boolean {
  if (!isBrowser()) return false;
  if (detectIos()) return false;
  return /Android/i.test(navigator.userAgent);
}

/** Mobile web: phone/tablet UA, coarse pointer, or &lt; md viewport. Hide on desktop. */
export function detectMobileWeb(): boolean {
  if (!isBrowser()) return false;
  if (detectIos()) return true;
  if (detectAndroidInstallContext()) return true;
  if (mediaMatches('(max-width: 767px)')) return true;
  if (mediaMatches('(pointer: coarse)')) return true;
  return false;
}

export function getInstallBannerDismissedAt(
  key: string = INSTALL_DISMISS_KEY
): string | null {
  if (!isBrowser()) return null;
  try {
    const value = localStorage.getItem(key);
    return value?.trim() ? value : null;
  } catch {
    return null;
  }
}

export function clearInstallBannerDismiss(key: string = INSTALL_DISMISS_KEY): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

export function isInstallBannerDismissed(
  nowMs: number = Date.now(),
  key: string = INSTALL_DISMISS_KEY
): boolean {
  const raw = getInstallBannerDismissedAt(key);
  if (!raw) return false;

  const dismissedAt = Date.parse(raw);
  if (Number.isNaN(dismissedAt)) {
    clearInstallBannerDismiss(key);
    return false;
  }

  if (nowMs - dismissedAt > INSTALL_BANNER_DISMISS_TTL_MS) {
    clearInstallBannerDismiss(key);
    return false;
  }

  return true;
}

export function dismissInstallBanner(
  at: Date = new Date(),
  key: string = INSTALL_DISMISS_KEY
): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(key, at.toISOString());
  } catch {
    // ignore storage failures
  }
}

export function resetInstallBannerDismiss(key: string = INSTALL_DISMISS_KEY): void {
  clearInstallBannerDismiss(key);
}

export type UsePwaInstallOptions = {
  dismissKey?: string;
};

export function usePwaInstall(options: UsePwaInstallOptions = {}) {
  const dismissKey = options.dismissKey ?? INSTALL_DISMISS_KEY;
  const [canPromptInstall, setCanPromptInstall] = useState(() => hasDeferredInstallPrompt());
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [isAndroidInstallContext, setIsAndroidInstallContext] = useState(false);
  const [isMobileWeb, setIsMobileWeb] = useState(false);

  useEffect(() => {
    if (!isBrowser()) return;

    ensureInstallPromptListener();
    setCanPromptInstall(hasDeferredInstallPrompt());
    setIsStandalone(detectStandaloneMode());
    setIsIos(detectIos());
    setIsIosSafari(detectIosSafari());
    setIsAndroidInstallContext(detectAndroidInstallContext());
    setIsMobileWeb(detectMobileWeb());

    const unsubscribePrompt = subscribeDeferredInstallPrompt(() => {
      setCanPromptInstall(hasDeferredInstallPrompt());
    });

    const onDisplayModeChange = () => setIsStandalone(detectStandaloneMode());
    const standaloneQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)')
        : null;

    standaloneQuery?.addEventListener?.('change', onDisplayModeChange);

    return () => {
      unsubscribePrompt();
      standaloneQuery?.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    return promptDeferredInstall();
  }, []);

  const dismiss = useCallback(
    (at?: Date) => {
      dismissInstallBanner(at ?? new Date(), dismissKey);
    },
    [dismissKey]
  );

  const resetDismiss = useCallback(() => {
    resetInstallBannerDismiss(dismissKey);
  }, [dismissKey]);

  const isDismissed = useCallback(
    (nowMs?: number) => isInstallBannerDismissed(nowMs ?? Date.now(), dismissKey),
    [dismissKey]
  );

  return {
    canPromptInstall,
    isStandalone,
    isIos,
    isIosSafari,
    isAndroidInstallContext,
    isMobileWeb,
    promptInstall,
    dismissInstallBanner: dismiss,
    resetInstallBannerDismiss: resetDismiss,
    isInstallBannerDismissed: isDismissed,
  };
}
